import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Autocomplete,
  CircularProgress,
  Divider,
  Alert,
  InputAdornment,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Stack,
  Collapse,
  IconButton,
  Badge,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Description as DescriptionIcon,
  History as HistoryIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import Sidebar from "../Sidebar/Sidebar";
import useAuth from "../../hooks/useAuth";
import {
  lookupCompany,
  lookupCompanyDetail,
  createLead,
  listLeads,
  sendLeadOffer,
  sendLeadContract,
  acceptLeadContract,
  uploadLeadContract,
  downloadLeadContract,
  createLeadAccess,
  fetchInbound,
  markLeadMessagesSeen,
} from "../../services/tenantSuperadminService";

const LICENSE_OPTIONS = [
  { code: "A1", label: "A1 – 1 prístup, do 5 dok./mes. – 19 €/mes." },
  { code: "A2", label: "A2 – 1–2 prístupy, do 40 dok./mes. – 39 €/mes." },
  { code: "A3", label: "A3 – 1–3 prístupy, do 80 dok./mes. – 79 €/mes." },
  { code: "A4", label: "A4 – viac prístupov, do 250 dok./mes. – 199 €/mes." },
  { code: "A5", label: "A5 – Individuálny plán (na mieru)" },
];

const fmtDt = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleString("sk-SK", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return s;
  }
};

// jednoduchý slug z názvu firmy (bez diakritiky, bez právnych foriem)
const slugify = (name) => {
  let s = (name || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  s = s.replace(/\b(s\.?\s?r\.?\s?o\.?|a\.?\s?s\.?|spol\.?|k\.?\s?s\.?|j\.?\s?s\.?\s?a\.?)\b/g, " ");
  s = s.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return s.slice(0, 40);
};

const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < 12; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out + "!9";
};

const emptyForm = {
  name: "",
  slug: "",
  ico: "",
  dic: "",
  ic_dph: "",
  contact_company_address: "",
  contact_company_postal_code: "",
  contact_company_town: "",
  contact_company_country: "",
  contact_admin_first_name: "",
  contact_admin_last_name: "",
  contact_admin_phone: "",
  contact_admin_email: "",
  admin_password: "",
};

export default function OnboardingPage() {
  const { isLoggedIn, user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);

  // listing organizácií
  const [tenants, setTenants] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [offerSendingId, setOfferSendingId] = useState(null);
  // potvrdenie opätovného odoslania ponuky
  const [offerConfirmLead, setOfferConfirmLead] = useState(null);
  // dialóg návrhu zmluvy
  const [contractLead, setContractLead] = useState(null);
  const [contractLicense, setContractLicense] = useState("A2");
  const [a5Docs, setA5Docs] = useState("");
  const [a5Accounts, setA5Accounts] = useState("");
  const [a5Overage, setA5Overage] = useState("");
  const [a5Price, setA5Price] = useState("");
  const [a5Until, setA5Until] = useState("");
  const [contractSending, setContractSending] = useState(false);
  const [contractAccepting, setContractAccepting] = useState(false);
  const [contractFile, setContractFile] = useState(null);
  const [contractUploading, setContractUploading] = useState(false);
  const [accessCreating, setAccessCreating] = useState(false);
  // dialóg logu
  const [logLead, setLogLead] = useState(null);
  // rozbalený detail v listingu
  const [expandedId, setExpandedId] = useState(null);
  // popup prijatých správ
  const [msgLead, setMsgLead] = useState(null);
  const [inboxLoading, setInboxLoading] = useState(false);

  const [form, setForm] = useState(emptyForm);
  const debTimer = useRef(null);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await listLeads();
      const list = Array.isArray(data) ? data : data?.results || [];
      setTenants(list);
    } catch (e) {
      setTenants([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  const refreshInbox = useCallback(async (notify = false) => {
    setInboxLoading(true);
    try {
      const res = await fetchInbound();
      await loadList();
      if (notify) {
        enqueueSnackbar(
          t("onboarding.inbox.fetched", { defaultValue: "Načítané. Nové správy: {{n}}", n: res?.new_messages ?? 0 }),
          { variant: "success" }
        );
      }
    } catch (e) {
      if (notify) enqueueSnackbar(t("onboarding.inbox.failed", "Načítanie e-mailov zlyhalo."), { variant: "error" });
    } finally {
      setInboxLoading(false);
    }
  }, [loadList, enqueueSnackbar, t]);

  useEffect(() => {
    if (isLoggedIn) {
      loadList();
      refreshInbox(false);
    }
  }, [isLoggedIn, loadList, refreshInbox]);

  const openMessages = async (tn) => {
    setMsgLead(tn);
    if ((tn.messages || []).some((m) => !m.seen)) {
      try {
        await markLeadMessagesSeen(tn.id);
        loadList();
      } catch (e) { /* ignore */ }
    }
  };

  const onField = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  // ---- Autocomplete: hľadanie od 3 znakov (debounce) ----
  const handleInput = useCallback((_e, value, reason) => {
    if (reason === "reset") return;
    const q = (value || "").trim();
    if (debTimer.current) clearTimeout(debTimer.current);
    if (q.length < 3) {
      setOptions([]);
      return;
    }
    debTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await lookupCompany(q);
        setOptions(Array.isArray(data?.results) ? data.results : []);
      } catch (e) {
        setOptions([]);
      } finally {
        setSearching(false);
      }
    }, 250);
  }, []);

  // ---- výber firmy: doplní identitu + dohľadá DIČ/IČ DPH ----
  const handlePick = async (_e, company) => {
    if (!company) return;
    const slug = slugify(company.name);
    setForm((f) => ({
      ...f,
      name: company.name || "",
      slug,
      ico: company.ico || "",
      dic: "",
      ic_dph: "",
      contact_company_address: "",
      contact_company_postal_code: "",
      contact_company_town: "",
      contact_company_country: "",
      admin_password: f.admin_password || genPassword(),
    }));

    if (company.ico) {
      setEnriching(true);
      try {
        const d = await lookupCompanyDetail(company.ico);
        setForm((f) => ({
          ...f,
          dic: d?.dic || "",
          ic_dph: d?.ic_dph || "",
          contact_company_address: d?.street || "",
          contact_company_postal_code: d?.postal_code || "",
          contact_company_town: d?.town || "",
          contact_company_country: d?.country || "",
        }));
      } catch (e) {
        enqueueSnackbar(
          t("onboarding.snackbar.taxFailed", "Údaje firmy sa nepodarilo dohľadať, doplňte ručne."),
          { variant: "warning" }
        );
      } finally {
        setEnriching(false);
      }
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      enqueueSnackbar(t("onboarding.validation.company", "Najprv vyberte firmu."), { variant: "error" });
      return;
    }
    setCreating(true);
    try {
      await createLead({
        name: form.name,
        ico: form.ico,
        dic: form.dic,
        ic_dph: form.ic_dph,
        contact_company_address: form.contact_company_address,
        contact_company_postal_code: form.contact_company_postal_code,
        contact_company_town: form.contact_company_town,
        contact_company_country: form.contact_company_country,
        contact_admin_first_name: form.contact_admin_first_name,
        contact_admin_last_name: form.contact_admin_last_name,
        contact_admin_phone: form.contact_admin_phone,
        contact_admin_email: form.contact_admin_email,
      });
      setCreated({ name: form.name });
      enqueueSnackbar(t("onboarding.snackbar.created", "Záznam bol pridaný do onboardingu."), { variant: "success" });
      loadList();
    } catch (e) {
      enqueueSnackbar(
        t("onboarding.snackbar.failed", { defaultValue: "Pridanie zlyhalo: {{err}}", err: e?.message || "chyba" }),
        { variant: "error" }
      );
    } finally {
      setCreating(false);
    }
  };

  const reset = () => {
    setForm(emptyForm);
    setOptions([]);
    setCreated(null);
  };

  const doSendOffer = async (tn) => {
    setOfferSendingId(tn.id);
    try {
      const res = await sendLeadOffer(tn.id);
      enqueueSnackbar(
        t("onboarding.offer.sent", { defaultValue: "Ponuka odoslaná na {{email}}", email: res?.email || tn.contact_admin_email }),
        { variant: "success" }
      );
      loadList();
    } catch (e) {
      enqueueSnackbar(
        t("onboarding.offer.failed", { defaultValue: "Ponuku sa nepodarilo odoslať: {{err}}", err: e?.message || "chyba" }),
        { variant: "error" }
      );
    } finally {
      setOfferSendingId(null);
    }
  };

  const handleSendOffer = async (tn) => {
    if (!tn?.contact_admin_email) {
      enqueueSnackbar(
        t("onboarding.offer.noEmail", "Organizácia nemá e-mail kontaktnej osoby."),
        { variant: "warning" }
      );
      return;
    }
    // Ak už bola ponuka odoslaná, najprv sa opýtať.
    if (tn.offer_sent_at) {
      setOfferConfirmLead(tn);
      return;
    }
    doSendOffer(tn);
  };

  const submitContract = async () => {
    if (!contractLead) return;
    setContractSending(true);
    try {
      const a5extra = contractLicense === "A5" ? {
        docs_limit: a5Docs, user_accounts: a5Accounts,
        overage_price: a5Overage, monthly_price: a5Price, valid_until: a5Until,
      } : {};
      const res = await sendLeadContract(contractLead.id, contractLicense, a5extra);
      enqueueSnackbar(
        t("onboarding.contract.sent", { defaultValue: "Návrh zmluvy ({{lic}}) odoslaný na {{email}}", lic: contractLicense, email: res?.email || contractLead.contact_admin_email }),
        { variant: "success" }
      );
      setContractLead(null);
      loadList();
    } catch (e) {
      enqueueSnackbar(
        t("onboarding.contract.failed", { defaultValue: "Návrh zmluvy sa nepodarilo odoslať: {{err}}", err: e?.message || "chyba" }),
        { variant: "error" }
      );
    } finally {
      setContractSending(false);
    }
  };

  // po akcii nad zmluvou obnoví zoznam a aktualizuje otvorený lead v dialógu
  const refreshContractLead = async () => {
    try {
      const data = await listLeads();
      const list = Array.isArray(data) ? data : data?.results || [];
      setTenants(list);
      setContractLead((cur) => (cur ? list.find((x) => x.id === cur.id) || cur : cur));
    } catch (e) { /* ignore */ }
  };

  const acceptContract = async () => {
    if (!contractLead) return;
    setContractAccepting(true);
    try {
      await acceptLeadContract(contractLead.id);
      enqueueSnackbar(t("onboarding.contract.accepted", "Zmluva označená ako akceptovaná."), { variant: "success" });
      await refreshContractLead();
    } catch (e) {
      enqueueSnackbar(t("onboarding.contract.acceptFailed", { defaultValue: "Akceptácia zlyhala: {{err}}", err: e?.message || "chyba" }), { variant: "error" });
    } finally {
      setContractAccepting(false);
    }
  };

  const uploadContract = async () => {
    if (!contractLead || !contractFile) return;
    setContractUploading(true);
    try {
      await uploadLeadContract(contractLead.id, contractFile);
      enqueueSnackbar(t("onboarding.contract.uploaded", "Originál zmluvy bol nahraný."), { variant: "success" });
      setContractFile(null);
      await refreshContractLead();
    } catch (e) {
      enqueueSnackbar(t("onboarding.contract.uploadFailed", { defaultValue: "Nahratie zlyhalo: {{err}}", err: e?.message || "chyba" }), { variant: "error" });
    } finally {
      setContractUploading(false);
    }
  };

  const createAccess = async () => {
    if (!contractLead) return;
    setAccessCreating(true);
    try {
      const res = await createLeadAccess(contractLead.id);
      enqueueSnackbar(
        t("onboarding.access.created", {
          defaultValue: "Organizácia vytvorená. Prihlasovacie meno: {{u}} · e-mail: {{em}} · SMS: {{sms}}",
          u: res?.admin_username || "",
          em: res?.email_sent ? "odoslaný" : "zlyhal",
          sms: res?.sms_sent ? "odoslaná" : `zlyhala (${res?.sms_info || ""})`,
        }),
        { variant: res?.sms_sent ? "success" : "warning", autoHideDuration: 12000 }
      );
      setContractLead(null);
      loadList();
    } catch (e) {
      enqueueSnackbar(
        t("onboarding.access.failed", { defaultValue: "Vytvorenie prístupov zlyhalo: {{err}}", err: e?.message || "chyba" }),
        { variant: "error" }
      );
    } finally {
      setAccessCreating(false);
    }
  };

  const openForm = () => {
    reset();
    setShowForm(true);
  };

  const backToList = () => {
    reset();
    setShowForm(false);
    loadList();
  };

  if (!isLoggedIn) return null;

  return (
    <>
      <Sidebar user={user} />

      <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
        <Box sx={{ width: "100%", maxWidth: 1200 }}>
          {!showForm ? (
            <Paper sx={{ p: 3, mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Box>
                  <Typography variant="h6">{t("onboarding.list.title", "Onboarding — organizácie")}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("onboarding.list.subtitle", "Zoznam pridaných organizácií")}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={inboxLoading ? <CircularProgress size={14} /> : <RefreshIcon />}
                    onClick={() => refreshInbox(true)}
                    disabled={inboxLoading}
                  >
                    {t("onboarding.inbox.refresh", "Načítať e-maily")}
                  </Button>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={openForm}>
                    {t("onboarding.list.add", "Pridať organizáciu")}
                  </Button>
                </Box>
              </Box>

              {loadingList ? (
                <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
                  <CircularProgress />
                </Box>
              ) : tenants.length === 0 ? (
                <Typography color="text.secondary">
                  {t("onboarding.list.empty", "Zatiaľ neboli pridané žiadne organizácie.")}
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 40 }} />
                        <TableCell>{t("tenantFormFields.name", "Názov")}</TableCell>
                        <TableCell>{t("tenantFormFields.ico", "IČO")}</TableCell>
                        <TableCell>{t("tenantFormFields.dic", "DIČ")}</TableCell>
                        <TableCell>{t("tenantContacts.company.town", "Mesto")}</TableCell>
                        <TableCell>{t("onboarding.list.contact", "Kontakt")}</TableCell>
                        <TableCell>{t("onboarding.list.email", "E-mail")}</TableCell>
                        <TableCell>{t("onboarding.list.lastState", "Posledný stav")}</TableCell>
                        <TableCell align="right">{t("onboarding.list.actions", "Akcie")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tenants.map((tn) => (
                        <React.Fragment key={tn.id}>
                        <TableRow hover sx={{ "& > *": { borderBottom: "unset" } }}>
                          <TableCell sx={{ width: 40 }}>
                            <IconButton size="small" onClick={() => setExpandedId(expandedId === tn.id ? null : tn.id)}>
                              {expandedId === tn.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              {tn.name}
                              {(tn.messages || []).length > 0 ? (
                                <Tooltip title={t("onboarding.inbox.hasReplies", "Prijaté odpovede")}>
                                  <IconButton size="small" color="error" onClick={() => openMessages(tn)}>
                                    <Badge
                                      badgeContent={(tn.messages || []).filter((m) => !m.seen).length || (tn.messages || []).length}
                                      color="error"
                                    >
                                      <EmailIcon fontSize="small" />
                                    </Badge>
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                            </Box>
                          </TableCell>
                          <TableCell>{tn.ico}</TableCell>
                          <TableCell>{tn.dic}</TableCell>
                          <TableCell>{tn.contact_company_town}</TableCell>
                          <TableCell>
                            {`${tn.contact_admin_first_name || ""} ${tn.contact_admin_last_name || ""}`.trim() || "—"}
                          </TableCell>
                          <TableCell>{tn.contact_admin_email || "—"}</TableCell>
                          <TableCell sx={{ color: "error.main", fontWeight: 600 }}>
                            {(() => {
                              const evs = tn.events || [];
                              const last = evs.length ? evs[evs.length - 1] : null;
                              return last ? `${last.event_type_display} · ${fmtDt(last.created_at)}` : "—";
                            })()}
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                sx={{ color: "#fff" }}
                                startIcon={offerSendingId === tn.id ? <CircularProgress size={14} color="inherit" /> : <SendIcon fontSize="small" />}
                                disabled={offerSendingId === tn.id || !tn.contact_admin_email}
                                onClick={() => handleSendOffer(tn)}
                              >
                                {tn.offer_sent_at
                                  ? t("onboarding.list.offerResend", "Ponuka ✓")
                                  : t("onboarding.list.offerSendBtn", "Poslať ponuku")}
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<DescriptionIcon fontSize="small" />}
                                disabled={!tn.contact_admin_email}
                                onClick={() => { setContractLead(tn); setContractLicense(tn.contract_license || "A2"); setContractFile(null); }}
                                sx={{ bgcolor: "#d32f2f", color: "#fff", "&:hover": { bgcolor: "#b71c1c" } }}
                              >
                                {tn.contract_sent_at
                                  ? t("onboarding.list.contractResend", "Zmluva ✓")
                                  : t("onboarding.list.contractSendBtn", "Poslať návrh zmluvy")}
                              </Button>
                              <Button
                                size="small"
                                startIcon={<HistoryIcon fontSize="small" />}
                                onClick={() => setLogLead(tn)}
                              >
                                {t("onboarding.list.log", "Log")}
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={9} sx={{ py: 0, borderBottom: expandedId === tn.id ? undefined : "none" }}>
                            <Collapse in={expandedId === tn.id} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, px: 1 }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                      {t("onboarding.detail.company", "Firma")}
                                    </Typography>
                                    <div><b>{t("tenantFormFields.name", "Názov")}:</b> {tn.name || "—"}</div>
                                    <div><b>IČO:</b> {tn.ico || "—"}</div>
                                    <div><b>DIČ:</b> {tn.dic || "—"}</div>
                                    <div><b>IČ DPH:</b> {tn.ic_dph || "—"}</div>
                                    <div><b>{t("tenantContacts.company.address", "Adresa")}:</b> {tn.contact_company_address || "—"}</div>
                                    <div><b>{t("tenantContacts.company.postalCode", "PSČ")}:</b> {tn.contact_company_postal_code || "—"} {tn.contact_company_town || ""}</div>
                                    <div><b>{t("tenantContacts.company.country", "Krajina")}:</b> {tn.contact_company_country || "—"}</div>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                      {t("onboarding.detail.contact", "Kontaktná osoba")}
                                    </Typography>
                                    <div><b>{t("onboarding.list.contact", "Meno")}:</b> {`${tn.contact_admin_first_name || ""} ${tn.contact_admin_last_name || ""}`.trim() || "—"}</div>
                                    <div><b>{t("tenantContacts.admin.phone", "Telefón")}:</b> {tn.contact_admin_phone || "—"}</div>
                                    <div><b>{t("tenantContacts.admin.email", "E-mail")}:</b> {tn.contact_admin_email || "—"}</div>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                      {t("onboarding.detail.milestones", "Stav onboardingu")}
                                    </Typography>
                                    <div><b>{t("onboarding.log.created", "Vytvorené")}:</b> {fmtDt(tn.created_at)}</div>
                                    <div><b>{t("onboarding.log.offer", "Ponuka")}:</b> {fmtDt(tn.offer_sent_at)}</div>
                                    <div><b>{t("onboarding.log.contract", "Návrh zmluvy")}:</b> {fmtDt(tn.contract_sent_at)}</div>
                                    <div><b>{t("onboarding.log.access", "Prístupové práva")}:</b> {fmtDt(tn.access_created_at)}</div>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          ) : (
            <Paper sx={{ p: 3, mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="h6">{t("onboarding.title", "Onboarding organizácie")}</Typography>
                <Button startIcon={<ArrowBackIcon />} onClick={backToList}>
                  {t("onboarding.backToList", "Späť na zoznam")}
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t(
                "onboarding.subtitle",
                "Zadajte aspoň 3 písmená názvu firmy. Systém dohľadá IČO, DIČ, IČ DPH a adresu. Potom doplňte kontaktnú osobu."
              )}
            </Typography>

            {created ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>
                  {t("onboarding.created.titleLead", "Záznam pridaný do onboardingu")}
                </Typography>
                <div>{t("onboarding.created.company", "Firma")}: <b>{created.name}</b></div>
                <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                  <Button variant="outlined" size="small" onClick={reset}>
                    {t("onboarding.created.another", "Pridať ďalšiu")}
                  </Button>
                  <Button size="small" onClick={backToList}>
                    {t("onboarding.backToList", "Späť na zoznam")}
                  </Button>
                </Box>
              </Alert>
            ) : (
              <>
                {/* 1) Vyhľadanie firmy */}
                <Autocomplete
                  fullWidth
                  filterOptions={(x) => x}
                  options={options}
                  loading={searching}
                  onInputChange={handleInput}
                  onChange={handlePick}
                  getOptionLabel={(o) =>
                    typeof o === "string" ? o : `${o.name} — IČO ${o.ico}${o.address_hint ? ", " + o.address_hint : ""}`
                  }
                  isOptionEqualToValue={(o, v) => o.ico === v.ico}
                  noOptionsText={t("onboarding.search.noOptions", "Zadajte aspoň 3 znaky…")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("onboarding.search.label", "Názov firmy / organizácie")}
                      placeholder={t("onboarding.search.placeholder", "napr. ESET")}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <>
                            {searching ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                {/* 2) Dohľadané údaje */}
                <Box sx={{ mt: 3, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {t("onboarding.identity.title", "Údaje firmy")}
                  </Typography>
                  {enriching ? <CircularProgress size={16} /> : null}
                  <Chip size="small" label={t("onboarding.identity.production", "Účet: produkcia")} color="success" variant="outlined" />
                  <Chip size="small" label={t("onboarding.identity.bankTransfer", "Platba: bankový prevod")} variant="outlined" />
                </Box>

                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextField fullWidth label={t("tenantFormFields.name", "Názov")} name="name" value={form.name} onChange={onField} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label={t("tenantFormFields.ico", "IČO")} name="ico" value={form.ico} onChange={onField} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label={t("tenantFormFields.dic", "DIČ")} name="dic" value={form.dic} onChange={onField} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label={t("tenantFormFields.icDph", "IČ DPH")} name="ic_dph" value={form.ic_dph} onChange={onField} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label={t("tenantContacts.company.address", "Adresa")} name="contact_company_address" value={form.contact_company_address} onChange={onField} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label={t("tenantContacts.company.postalCode", "PSČ")} name="contact_company_postal_code" value={form.contact_company_postal_code} onChange={onField} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label={t("tenantContacts.company.town", "Mesto")} name="contact_company_town" value={form.contact_company_town} onChange={onField} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label={t("tenantContacts.company.country", "Krajina")} name="contact_company_country" value={form.contact_company_country} onChange={onField} />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* 3) Kontaktná osoba */}
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  {t("onboarding.contact.title", "Kontaktná osoba")}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label={t("tenantContacts.admin.firstName", "Meno")} name="contact_admin_first_name" value={form.contact_admin_first_name} onChange={onField} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label={t("tenantContacts.admin.lastName", "Priezvisko")} name="contact_admin_last_name" value={form.contact_admin_last_name} onChange={onField} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label={t("tenantContacts.admin.phone", "Telefón")} name="contact_admin_phone" value={form.contact_admin_phone} onChange={onField} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth type="email" label={t("tenantContacts.admin.email", "E-mail")} name="contact_admin_email" value={form.contact_admin_email} onChange={onField} />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1 }}>
                  <Button onClick={reset} disabled={creating}>
                    {t("onboarding.actions.reset", "Vymazať")}
                  </Button>
                  <Button variant="contained" onClick={handleCreate} disabled={creating || !form.name}>
                    {creating ? t("onboarding.actions.creating", "Ukladám…") : t("onboarding.actions.create", "Pridať do onboardingu")}
                  </Button>
                </Box>
              </>
            )}
            </Paper>
          )}
        </Box>
      </Box>

      {/* Dialóg – návrh zmluvy (odoslanie / akceptácia / nahratie originálu) */}
      <Dialog open={!!contractLead} onClose={() => (contractSending ? null : setContractLead(null))} fullWidth maxWidth="sm">
        <DialogTitle>{t("onboarding.contract.title", "Návrh zmluvy")}</DialogTitle>
        <DialogContent dividers>
          {contractLead?.contract_sent_at ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t("onboarding.contract.already", {
                defaultValue: "Návrh zmluvy už bol odoslaný {{date}}{{lic}}. Môžete ho poslať znova (zvoľte program) alebo zmluvu akceptovať.",
                date: fmtDt(contractLead.contract_sent_at),
                lic: contractLead.contract_license ? ` (${contractLead.contract_license})` : "",
              })}
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("onboarding.contract.help", "Vyberte typ licencie. Zmluva sa predvyplní podľa tarify a údajov organizácie, termín od dnešného dňa; podpis odberateľa ostane prázdny.")}
            </Typography>
          )}

          <TextField
            select
            fullWidth
            label={t("onboarding.contract.license", "Typ licencie / program")}
            value={contractLicense}
            onChange={(e) => setContractLicense(e.target.value)}
            disabled={contractSending}
          >
            {LICENSE_OPTIONS.map((o) => (
              <MenuItem key={o.code} value={o.code}>{o.label}</MenuItem>
            ))}
          </TextField>

          {contractLicense === "A5" ? (
            <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <TextField type="number" label="Počet súborov anonymizácie / mes." value={a5Docs} onChange={(e) => setA5Docs(e.target.value)} disabled={contractSending} inputProps={{ min: 0 }} />
              <TextField type="number" label="Počet užívateľských kont" value={a5Accounts} onChange={(e) => setA5Accounts(e.target.value)} disabled={contractSending} inputProps={{ min: 0 }} />
              <TextField label="Cena po prekročení za dokument" value={a5Overage} onChange={(e) => setA5Overage(e.target.value)} disabled={contractSending} placeholder="napr. 0,15 €" />
              <TextField label="Mesačná cena (individuálna)" value={a5Price} onChange={(e) => setA5Price(e.target.value)} disabled={contractSending} placeholder="napr. 350 €/mes" />
              <TextField type="date" label="Čas licencie do" value={a5Until} onChange={(e) => setA5Until(e.target.value)} disabled={contractSending} InputLabelProps={{ shrink: true }} />
            </Box>
          ) : null}
          {contractLead ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              {t("onboarding.contract.recipient", "Príjemca")}: {contractLead.name} — {contractLead.contact_admin_email}
            </Typography>
          ) : null}

          {/* Akceptácia + nahratie originálu (až keď bola zmluva odoslaná) */}
          {contractLead?.contract_sent_at ? (
            <>
              <Divider sx={{ my: 2 }} />
              {contractLead?.contract_accepted_at ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {t("onboarding.contract.acceptedAt", { defaultValue: "Zmluva akceptovaná {{date}}.", date: fmtDt(contractLead.contract_accepted_at) })}
                </Alert>
              ) : (
                <Button
                  variant="outlined"
                  color="success"
                  onClick={acceptContract}
                  disabled={contractAccepting}
                  startIcon={contractAccepting ? <CircularProgress size={14} /> : null}
                  sx={{ mb: 2 }}
                >
                  {t("onboarding.contract.acceptBtn", "Zmluva bola akceptovaná")}
                </Button>
              )}

              {contractLead?.contract_accepted_at ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    {t("onboarding.contract.uploadTitle", "Originál zmluvy")}
                  </Typography>
                  {contractLead?.has_signed_contract ? (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        {t("onboarding.contract.uploadedFile", "Nahraté")}: <b>{contractLead.signed_contract_filename}</b> ({fmtDt(contractLead.signed_contract_uploaded_at)})
                        {" "}
                        <Button size="small" onClick={() => downloadLeadContract(contractLead.id, contractLead.signed_contract_filename)}>
                          {t("onboarding.contract.download", "Stiahnuť")}
                        </Button>
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                      {t("onboarding.contract.uploadNeeded", "Po akceptácii je potrebné nahrať originál zmluvy do systému.")}
                    </Typography>
                  )}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Button variant="outlined" component="label" disabled={contractUploading}>
                      {t("onboarding.contract.chooseFile", "Vybrať súbor")}
                      <input type="file" hidden accept=".pdf,.docx,.doc,.jpg,.jpeg,.png"
                        onChange={(e) => setContractFile(e.target.files?.[0] || null)} />
                    </Button>
                    {contractFile ? <Typography variant="caption">{contractFile.name}</Typography> : null}
                    <Button variant="contained" onClick={uploadContract} disabled={!contractFile || contractUploading}
                      startIcon={contractUploading ? <CircularProgress size={14} /> : null}>
                      {t("onboarding.contract.uploadBtn", "Nahrať originál")}
                    </Button>
                  </Box>
                </Box>
              ) : null}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContractLead(null)} disabled={contractSending || accessCreating}>
            {t("onboarding.actions.close", "Zavrieť")}
          </Button>
          {contractLead?.access_created_at ? (
            <Button variant="contained" color="success" sx={{ color: "#fff" }} disabled>
              {t("onboarding.access.done", "Prístupy vytvorené")}
            </Button>
          ) : (contractLead?.contract_accepted_at && contractLead?.has_signed_contract) ? (
            <Button
              variant="contained"
              color="success"
              sx={{ color: "#fff" }}
              onClick={createAccess}
              disabled={accessCreating}
              startIcon={accessCreating ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {t("onboarding.access.create", "Vytvoriť prístupy")}
            </Button>
          ) : (
            <Button variant="contained" onClick={submitContract} disabled={contractSending}
              sx={{ bgcolor: "#d32f2f", color: "#fff", "&:hover": { bgcolor: "#b71c1c" } }}
              startIcon={contractSending ? <CircularProgress size={14} color="inherit" /> : <DescriptionIcon fontSize="small" />}>
              {contractLead?.contract_sent_at
                ? t("onboarding.contract.resubmit", "Poslať znova")
                : t("onboarding.contract.submit", "Odoslať návrh zmluvy")}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialóg – log / timeline */}
      <Dialog open={!!logLead} onClose={() => setLogLead(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {t("onboarding.log.title", "Log onboardingu")}{logLead ? ` — ${logLead.name}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1} sx={{ mb: 2 }}>
            <div><b>{t("onboarding.log.created", "Záznam vytvorený")}:</b> {fmtDt(logLead?.created_at)}</div>
            <div><b>{t("onboarding.log.offer", "Ponuka odoslaná")}:</b> {fmtDt(logLead?.offer_sent_at)}</div>
            <div><b>{t("onboarding.log.contract", "Návrh zmluvy odoslaný")}:</b> {fmtDt(logLead?.contract_sent_at)}</div>
            <div><b>{t("onboarding.log.access", "Prístupové práva vytvorené")}:</b> {fmtDt(logLead?.access_created_at)}</div>
          </Stack>
          <Divider sx={{ mb: 1 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t("onboarding.log.timeline", "Časová os udalostí")}</Typography>
          {(logLead?.events || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">{t("onboarding.log.empty", "Žiadne udalosti.")}</Typography>
          ) : (
            <Stack spacing={0.5}>
              {(logLead?.events || []).map((ev) => (
                <Box key={ev.id} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                  <span>{ev.event_type_display}{ev.note ? ` (${ev.note})` : ""}{ev.actor ? ` — ${ev.actor}` : ""}</span>
                  <span style={{ whiteSpace: "nowrap", color: "#888" }}>{fmtDt(ev.created_at)}</span>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogLead(null)}>{t("onboarding.actions.close", "Zavrieť")}</Button>
        </DialogActions>
      </Dialog>

      {/* Dialóg – potvrdenie opätovného odoslania ponuky */}
      <Dialog open={!!offerConfirmLead} onClose={() => setOfferConfirmLead(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("onboarding.offerConfirm.title", "Ponuka už bola odoslaná")}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {t("onboarding.offerConfirm.text", {
              defaultValue: "Ponuka pre {{name}} už bola odoslaná {{date}} na {{email}}. Chcete ju poslať ešte raz?",
              name: offerConfirmLead?.name || "",
              date: fmtDt(offerConfirmLead?.offer_sent_at),
              email: offerConfirmLead?.contact_admin_email || "",
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOfferConfirmLead(null)}>
            {t("onboarding.actions.no", "Nie")}
          </Button>
          <Button
            variant="contained"
            color="success"
            sx={{ color: "#fff" }}
            onClick={() => { const l = offerConfirmLead; setOfferConfirmLead(null); if (l) doSendOffer(l); }}
          >
            {t("onboarding.offerConfirm.yes", "Áno, poslať znova")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialóg – prijaté e-maily od kontaktu */}
      <Dialog open={!!msgLead} onClose={() => setMsgLead(null)} fullWidth maxWidth="md">
        <DialogTitle>
          {t("onboarding.inbox.title", "Prijaté správy")}{msgLead ? ` — ${msgLead.name}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {(msgLead?.messages || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("onboarding.inbox.empty", "Žiadne prijaté správy.")}
            </Typography>
          ) : (
            <Stack spacing={2}>
              {(msgLead?.messages || []).map((m) => (
                <Paper key={m.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {m.subject || t("onboarding.inbox.noSubject", "(bez predmetu)")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {m.from_email} · {fmtDt(m.received_at)}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {m.body || "—"}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMsgLead(null)}>{t("onboarding.actions.close", "Zavrieť")}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
